<template>
    <div class="forum-search-advanced-options">
        <button
            class="btn-header"
            @click="toggleMenu()">
            <span>Advanced Options</span>
            <i
                class="fa fa-chevron-down"
                v-show="!menuOpen"></i>
            <i
                class="fa fa-chevron-up"
                v-show="menuOpen"></i>
        </button>
        <div
            class="advanced-options-body"
            v-show="menuOpen">

            <div class="option-group">
                <div class="option-heading">
                    Categories
                </div>
                <ul class="option-group-list">
                    <li
                        class="option-group-list-item"
                        v-for="category in sortedSelectedCategories">
                        <div class="flex">
                            <div class="flex-full">
                                {{ category.label }}
                            </div>
                            <button @click="deselectCategory(category.id)">
                                <i class="fa fa-times"></i>
                            </button>
                        </div>
                    </li>
                </ul>
                <div
                    class="option-group-list-spacer"
                    v-if="selectedCategories.length === 0">
                </div>
                <SearchSelect
                    ref="categorySearchSelect"
                    :options="categoryOptions"
                    :multiple="true"
                    :value="selectedCategoryIds"
                    @input="updateSelectedCategories($event)">
                </SearchSelect>
            </div>

            <div class="term-type-option-group option-group">
                <div class="option-heading">
                    Term Types
                </div>
                <Multiselect
                    :options="termTypeOptions"
                    :value="selectedTermTypes"
                    @input="updateSelectedTermTypes($event)">
                </Multiselect>
            </div>

            <div class="path-option-group option-group">
                <div class="option-heading">
                    Paths
                </div>
                <Multiselect
                    :options="pathOptions"
                    :value="selectedPathIds"
                    @input="updateSelectedPaths($event)">
                </Multiselect>
            </div>

            <div class="assignment-option-group option-group">
                <div class="option-heading">
                    Assignments
                </div>
                <ul class="option-group-list">
                    <li
                        class="option-group-list-item"
                        v-for="name in sortedSelectedAssignmentNames">
                        <div class="flex">
                            <div class="name flex-full">
                                {{ name }}
                            </div>
                            <button @click="deselectAssignment(name)">
                                <i class="fa fa-times"></i>
                            </button>
                        </div>
                    </li>
                </ul>
                <div
                    class="option-group-list-spacer"
                    v-if="selectedAssignments.length === 0">
                </div>
                <SearchSelect
                    ref="assignmentSearchSelect"
                    :options="assignmentOptions"
                    :multiple="true"
                    :value="normalizedSelectedAssignmentNames"
                    @input="updateSelectedAssignments($event)">
                </SearchSelect>
            </div>

        </div>
    </div>
</template>

<script>
    // ForumSearchAdvancedOptions: wraps the "Advanced Options" section of the
    // forum search page. Allows a user to select terms, categories,
    // assignments, etc. to filter their search results by.

    import Multiselect from '../reusable/multiselect';
    import SearchSelect from '../reusable/search-select';
    import StringUtils from 'src/utils/strings';
    import TermUtils from 'src/utils/terms';

    function normalizeAssignmentName(name) {
        return name.trim().toLowerCase();
    }

    function groupAssignmentsByName(assignments) {
        const assignmentsByName = {};
        for (const a of assignments) {
            const name = normalizeAssignmentName(a.name);
            if (!assignmentsByName[name]) {
                assignmentsByName[name] = [a];
            } else {
                assignmentsByName[name].push(a);
            }
        }
        return assignmentsByName;
    }

    function sortCategories(categories) {
        // Categories should be sorted alphabetically.
        categories = categories.slice();
        return categories.sort((a, b) => a.label.localeCompare(b.label));
    }

    function sortTermTypes(termTypes) {
        return TermUtils.sortTypes(termTypes);
    }

    function sortPaths(paths) {
        // Paths don't need to be sorted (yet).
        return paths.slice();
    }

    function sortAssignmentNames(assignmentNames) {
        // Assignments should be sorted alphabetically.
        assignmentNames = assignmentNames.slice();
        return assignmentNames.sort((a, b) => a.localeCompare(b));
    }

    export default {
        props: {
            allCategories: {
                type: Array,
            },
            allTermTypes: {
                type: Array,
            },
            allPaths: {
                type: Array,
            },
            allAssignments: {
                type: Array,
            },
            selectedCategoryIds: {
                type: Array,
            },
            selectedTermTypes: {
                type: Array,
            },
            selectedPathIds: {
                type: Array,
            },
            selectedAssignmentIds: {
                type: Array,
            },
        },

        components: {
            Multiselect,
            SearchSelect,
        },

        data() {
            return {
                menuOpen: false,
            };
        },

        methods: {
            toggleMenu() {
                this.menuOpen = !this.menuOpen;
            },

            updateSelectedCategories(categoryIds) {
                // Sort the category ids; this is important to provide a
                // consistent warning message below the forum search input.
                let categories = categoryIds.map(cId => {
                    return this.allCategories.find(c => c.id === cId);
                }).filter(c => !!c);
                categories = sortCategories(categories);
                categoryIds = categories.map(c => c.id);

                this.$emit('update:selectedCategoryIds', categoryIds);
            },

            updateSelectedTermTypes(termTypes) {
                // Sort the term types; see note above.
                termTypes = sortTermTypes(termTypes);

                this.$emit('update:selectedTermTypes', termTypes);
            },

            updateSelectedPaths(pathIds) {
                // Sort the path ids; see note above.
                let paths = pathIds.map(pId => {
                    return this.allPaths.find(p => p.id === pId);
                }).filter(p => !!p);
                paths = sortPaths(paths);
                pathIds = paths.map(p => p.id);

                this.$emit('update:selectedPathIds', pathIds);
            },

            updateSelectedAssignments(normAssignmentNames) {
                // Expand and sort assignment names
                normAssignmentNames = sortAssignmentNames(normAssignmentNames);
                const byName = groupAssignmentsByName(this.allAssignments);
                let assignments = [];
                for (const normName of normAssignmentNames) {
                    assignments = assignments.concat(byName[normName]);
                }
                const assignmentIds = assignments.map(a => a.id);

                this.$emit('update:selectedAssignmentIds', assignmentIds);
            },

            deselectCategory(categoryId) {
                const currentIds = this.selectedCategoryIds;
                if (!currentIds.includes(categoryId)) {
                    return;
                }
                const newIds = currentIds.filter(id => id !== categoryId);
                this.updateSelectedCategories(newIds);
            },

            deselectAssignment(name) {
                name = normalizeAssignmentName(name);
                const currentNames = this.normalizedSelectedAssignmentNames;
                if (!currentNames.includes(name)) {
                    return;
                }
                const newNames = currentNames.filter(n => n !== name);
                this.updateSelectedAssignments(newNames);
            },
        },

        computed: {
            selectedCategories() {
                return this.selectedCategoryIds.map(catId => {
                    return this.allCategories.find(cat => cat.id === catId);
                }).filter(c => !!c);
            },

            sortedSelectedCategories() {
                return sortCategories(this.selectedCategories);
            },

            selectedPaths() {
                return this.selectedPathIds.map(pathId => {
                    return this.allPaths.find(path => path.id === pathId);
                }).filter(p => !!p);
            },

            selectedAssignments() {
                return this.selectedAssignmentIds.map(aId => {
                    return this.allAssignments.find(a => a.id === aId);
                }).filter(a => !!a);
            },

            selectedAssignmentNames() {
                const byName = groupAssignmentsByName(this.selectedAssignments);
                const normalizedNames = Object.keys(byName);
                return normalizedNames.map(normName => {
                    return byName[normName][0].name;
                });
            },

            sortedSelectedAssignmentNames() {
                return sortAssignmentNames(this.selectedAssignmentNames);
            },

            normalizedSelectedAssignmentNames() {
                return this.selectedAssignmentNames.map(name => {
                    return normalizeAssignmentName(name);
                });
            },

            categoryOptions() {
                const prunedCategories = this.allCategories.filter(cat => {
                    return !this.selectedCategoryIds.includes(cat.id);
                });
                const sortedCategories = sortCategories(prunedCategories);
                return sortedCategories.map(c => {
                    return {
                        value: c.id,
                        label: c.label,
                    };
                });
            },

            termTypeOptions() {
                const sortedTermTypes = sortTermTypes(this.allTermTypes);
                return sortedTermTypes.map(tt => {
                    return {
                        value: tt,
                        label: StringUtils.toTitleCase(tt),
                    };
                });
            },

            pathOptions() {
                const sortedPaths = sortPaths(this.allPaths);
                return sortedPaths.map(p => {
                    return {
                        value: p.id,
                        label: p.name,
                    };
                });
            },

            assignmentOptions() {
                // Some assignments have the same name; we want to condense
                // those assignments into a single option. At the time of
                // development, this condensing step reduces the number of
                // options from over 600 to about 150, so it's quite
                // substantial.
                //
                // TODO: extract this functionality to forum utils, or better
                // yet, to some assignment utils, since it's needed across
                // multiple features. It might be worth checking out the other
                // implementations in places like the gallery filters.
                const prunedAssignments = this.allAssignments.filter(a => {
                    return !this.selectedAssignmentIds.includes(a.id);
                });
                const byName = groupAssignmentsByName(prunedAssignments);
                const normalizedNames = Object.keys(byName);
                const sortedNames = sortAssignmentNames(normalizedNames);
                return sortedNames.map(normName => {
                    const firstAssignment = byName[normName][0];
                    return {
                        value: normName,
                        label: firstAssignment.name,
                    };
                });
            },
        },
    }
</script>

<style lang="scss" scoped>
    .flex {
        display: flex;
    }
    .flex-full {
        flex: 1;
    }
    .flex-none {
        flex: 0;
    }

    .forum-search-advanced-options {
        width: 285px;
        margin-left: 40px;
        background: #333;
    }

    .btn-header {
        border: none;
        background: none;
        display: flex;
        width: 100%;
        height: 45px;
        text-align: left;
        padding: 10px 15px;

        span {
            flex: 1;
        }

        i {
            position: relative;
            top: 1px;
        }
    }

    .advanced-options-body {
        border-top: 1px solid #444;
        padding: 0 15px 15px;
    }

    .option-group {
        margin-top: 20px;

        .option-heading {
        }

        .option-group-list {
            list-style: disc;
            padding: 0 0 0 20px;
            margin: 5px 0;

            .name {
                padding: 4px 0 5px;
            }

            button {
                background: none;
                border: none;
                padding: 0;
                margin: 2px;
                flex: 0 0 24px;
                height: 24px;
                line-height: 24px;

                &:hover {
                    background: #444;
                }
            }
        }

        .option-group-list-spacer {
            margin-top: 10px;
        }
    }
</style>

<style lang="scss">
    .forum-search-advanced-options {
        .multiselect {
            button {
                background-color: #1e1e1e;
                width: 100%;
                height: 28px;
                line-height: 26px; // 28px - border
                padding: 0 10px;

                &.selected {
                    background: #b0b0b0;
                    color: #161616;
                }
            }
        }

        .advanced-options-body .search-select { // extra specificity needed
            margin-top: 5px;
            height: 34px; // 32px + border
            line-height: 32px;
            background: #1e1e1e;
            border: 1px solid #484848;

            .option:not(.highlighted) {
                background: #1e1e1e;
            }
        }

        .term-type-option-group .multiselect {
            display: grid;
            grid-template-columns: 1fr 1fr;
            grid-gap: 10px;
            margin: 10px 0 0 0;
            li {
                display: block;
                margin: 0;
            }
        }

        .path-option-group .multiselect {
            margin: 0;
            li {
                display: block;
                margin: 10px 0 0;
            }
        }
    }
</style>



// WEBPACK FOOTER //
// ForumSearchAdvancedOptions.vue?77833d4e